import React, { useContext, useState } from 'react';
import { FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import Banner from '@cpg-its/ads.containers.banner';
import { AddressFields } from '../../../pages/account-info/models/form-models';
import { FormField } from './form-field';
import { FormGroup } from '../layout/form-group';
import { TcsmStates } from '../../../root/tcsm-model';
import { LabelSettingProps } from '../../models/epi-model';
import { ContentSpacer } from '../layout/generic-content-spacer';
import { parseLov } from '../../helpers/form-data-helper';
import { PersonState } from '../../models/lov-proc-model';
import { PerformPersonSearchContext, PersonSearchContext } from '../../../pages/arc/features/person-search/person-search-model';
import { handleOSCRRBlur } from '../../../pages/arc/features/person-search/oscrr-helpers';
import { DemographicsContext } from '../../models/demographics-model';
import { setDocToastHide, toggleDocumentUpload } from '../../../pages/account-info/features/personal-info/document-upload-slice';
import { InternationalAddressWarning, shouldShowDocUpload } from '../../helpers/doc-upload-helpers';
import { Person } from '../../models/person-model';
import { useIsReceivingPensionOrPayout } from '../../../pages/account-info/hooks/useIsReceivingPensionOrPayout';
import { FieldAutomationIds } from '../../constants/field-automation-ids';

interface AddressFieldProps {
	formProps: FormikProps<any>;
	currentCountry?: string;
	labels: AddressFields;
	addressFieldNames: AddressFields;
	requiredSchema: AddressFields;
	addressExpired?: boolean;
	addressExpiredError?: boolean;
	expiredAddressChangeToggle?: (addressChanged: boolean) => any;
	person?: boolean;
	officerSearch?: boolean;
	currentPerson?: Person;
	docUpload?: boolean;
	sameAddressCheckBox?: JSX.Element;
}

export function AddressField(props: AddressFieldProps) {
	const app = useContext(DemographicsContext);
	const { formProps, addressFieldNames, expiredAddressChangeToggle, addressExpired, addressExpiredError, officerSearch,
		currentPerson, docUpload, sameAddressCheckBox } = props;
	const personSearchKey = useContext(PersonSearchContext);
	const performPersonSearch = useContext(PerformPersonSearchContext);
	const country = useSelector<TcsmStates, any>(state => props.person ? state.lovProcState?.personLov?.country : state.lovProcState?.orgLov?.country);
	const countryOptions = country ? parseLov(country) : [];
	const states = useSelector<TcsmStates, PersonState>(state => props.person ? state.lovProcState?.personLov?.state : state.lovProcState?.orgLov?.state);
	const stateOptions = states ? parseLov(states.US) : [];
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps?.labelSettingState?.epiContent);
	const dispatch = useDispatch();
	const isReceivingPensionOrPayout = useIsReceivingPensionOrPayout(currentPerson?.person_guid);
	const [prevValue, setPrevValue] = useState({});
	let stateDisplay = stateOptions;
	if (props.currentCountry !== 'US') {
		stateDisplay = [];
	}
	if (props.currentCountry === 'MX') {
		stateDisplay = parseLov(states.MX);
	}
	if (props.currentCountry === 'CA') {
		stateDisplay = parseLov(states.CA);
	}

	const handleDocUpload = (formValues: any) => {
		if (docUpload) {
			dispatch(
				toggleDocumentUpload(
					shouldShowDocUpload(currentPerson, formValues),
					app,
					InternationalAddressWarning(currentPerson, formValues, addressFieldNames, isReceivingPensionOrPayout)));
			dispatch(setDocToastHide(false, app));
		}
	};

	const onFieldChange = (fieldName: string, value: string, automaticReset = false, triggerSearch = false) => {
		formProps.setFieldTouched(fieldName, true);
		formProps.setFieldValue(fieldName, automaticReset && !value ? prevValue[fieldName] : value);

		// Form  properties not always updated when 
		// the select is changed.
		const formValues = {
			...formProps.values,
		};

		formValues[fieldName] = value;

		handleDocUpload(formValues);

		// if the address has expired, check if any of the values have changed
		if (addressExpired) {
			let addressChanged = false;
			Object.values({ ...addressFieldNames }).forEach(key => {
				const compareValue = key === fieldName ? value : formProps.values[key as string];

				if (formProps.initialValues[key as string] !== compareValue) {
					addressChanged = true;
				}
			});

			expiredAddressChangeToggle(addressChanged);
		}

		// if country changes
		if (fieldName === props.addressFieldNames.country) {

			// if not a country with states, make state empty
			if (!(value === 'US' || value === 'MX' || value === 'CA')) {
				formProps.setFieldValue(props.addressFieldNames.state as string, '');

				if (props?.addressFieldNames?.province) {
					formProps.setFieldValue(props.addressFieldNames.province as string, '');
				}

				// if not US, make line 3 empty
				if (!(value === 'US') && props?.addressFieldNames?.address3) {
					formProps.setFieldValue(props.addressFieldNames.address3 as string, '');
				}
			}
		}

		if (performPersonSearch && triggerSearch) {
			handleOSCRRBlur(dispatch, formProps, personSearchKey, fieldName, value, officerSearch);
		}
	};

	const onFocus = (fieldName: string) => {

		if (formProps?.values[fieldName]?.includes('XXX')) {
			const copy = { ...prevValue };
			formProps.setFieldValue(fieldName, '');
			copy[fieldName] = formProps?.values[fieldName];
			setPrevValue(copy);
		}
	};

	const AddressExpiredMessage = () => {
		if (!addressExpiredError) return null;

		return (
			<ContentSpacer>
				<Banner
					banner={{
						type: 'warning',
						label: <p><strong>{labelSettings?.addressExpiredError || 'This address is expired and must be updated'}</strong></p>,
					}}
				/>
			</ContentSpacer>
		);
	};

	return (
		<FormGroup>
			<AddressExpiredMessage />
			{
				sameAddressCheckBox
			}
			<FormField
				name={props.addressFieldNames.country}
				label={props.labels.country}
				component='input'
				options={countryOptions/*countryOptions.sort(countrySort)*/}
				errors={props.formProps.errors}
				touched={props.formProps.touched}
				select
				required={props.requiredSchema.country}
				testId={FieldAutomationIds.countryDropdown}
				onChange={(e) => onFieldChange(props.addressFieldNames.country as string, e.target.value, false, true)}
			/>
			<FormField
				name={props.addressFieldNames.address1}
				label={props.labels.address1}
				component='input'
				errors={props.formProps.errors}
				touched={props.formProps.touched}
				required={props.requiredSchema.address1}
				onBlur={(e) => onFieldChange(props.addressFieldNames.address1 as string, e.target.value)}
				onFocus={() => onFocus(props.addressFieldNames.address1 as string)}
				testId={FieldAutomationIds.addressLine1}

			/>
			<FormField
				name={props.addressFieldNames.address2}
				label={props.labels.address2}
				component='input'
				errors={props.formProps.errors}
				touched={props.formProps.touched}
				required={props.requiredSchema.address2}
				onBlur={(e) => onFieldChange(props.addressFieldNames.address2 as string, e.target.value)}

			/>
			{
				props.currentCountry !== 'US' &&
				<FormField
					name={props.addressFieldNames.address3}
					label={props.labels.address3}
					component='input'
					errors={props.formProps.errors}
					touched={props.formProps.touched}
					required={props.requiredSchema.address3}
					onBlur={(e) => onFieldChange(props.addressFieldNames.address3 as string, e.target.value)}

				/>
			}
			<FormField
				name={props.addressFieldNames.city}
				label={props.labels.city}
				component='input'
				errors={props.formProps.errors}
				touched={props.formProps.touched}
				required={props.requiredSchema.city}
				onBlur={(e) => onFieldChange(props.addressFieldNames.city as string, e.target.value, false, true)}
				testId={FieldAutomationIds.city}

			/>
			{
				stateDisplay.length > 0 &&
				<FormField
					name={props.addressFieldNames.state}
					label={props.currentCountry === 'MX' || props.currentCountry === 'CA' ? props.labels.province : props.labels.state}
					component='input'
					options={stateDisplay}
					errors={props.formProps.errors}
					touched={props.formProps.touched}
					select
					required={props.requiredSchema.state}
					onChange={(e) => onFieldChange(props.addressFieldNames.state as string, e.target.value, false, true)}
					testId={FieldAutomationIds.stateDropdown}
				/>
			}

			<FormField
				name={props.addressFieldNames.postalCode}
				label={props.labels.postalCode}
				component='input'
				required={props.requiredSchema.postalCode}
				errors={props.formProps.errors}
				touched={props.formProps.touched}
				onBlur={(e) => onFieldChange(props.addressFieldNames.postalCode as string, e.target.value, false, true)}
				testId={FieldAutomationIds.postalCode}
			/>
		</FormGroup>
	);
}

AddressField.defaultProps = {
	addressExpired: false,
	addressExpiredError: false,
	expiredAddressChangeToggle: () => { },
	currentCountry: '',
	person: false,
	officerSearch: false,
	currentPerson: null,
	docUpload: false,
	sameAddressCheckBox: null,
};

